import { LiveData, Service } from '@toeverything/infra';

export type NavigationPanelRevealSource =
  | 'advanced-search'
  | 'compact-search'
  | 'doc-create'
  | 'journal'
  | 'notification'
  | 'unknown';

const SIDEBAR_STATE_PRESERVING_REVEAL_SOURCES: ReadonlySet<NavigationPanelRevealSource> =
  new Set(['advanced-search', 'compact-search']);

/**
 * Search openings only mark the doc in the tree; the user's sidebar
 * open/closed choice stays as it was.
 */
export function shouldNavigationPanelRevealOpenSidebar(
  source: NavigationPanelRevealSource
): boolean {
  return !SIDEBAR_STATE_PRESERVING_REVEAL_SOURCES.has(source);
}

export type NavigationPanelRevealRequest = {
  docId?: string;
  folderId?: string;
  id: number;
  openAfterRenameSubmit: boolean;
  rename: boolean;
  /** Survives presentation remounts until this reveal request finishes. */
  renameDraft: { value?: string };
  source: NavigationPanelRevealSource;
  workspaceId: string;
};

export class NavigationPanelRevealService extends Service {
  private nextRequestId = 0;

  readonly request$ = new LiveData<NavigationPanelRevealRequest | null>(null);

  revealDoc(
    docId: string,
    options: {
      openAfterRenameSubmit?: boolean;
      rename?: boolean;
      source?: NavigationPanelRevealSource;
      workspaceId: string;
    }
  ) {
    this.request$.next({
      docId,
      id: ++this.nextRequestId,
      openAfterRenameSubmit: options.openAfterRenameSubmit === true,
      rename: options.rename === true,
      renameDraft: {},
      source: options.source ?? 'unknown',
      workspaceId: options.workspaceId,
    });
  }

  revealFolder(
    folderId: string,
    options: {
      source?: NavigationPanelRevealSource;
      workspaceId: string;
    }
  ) {
    this.request$.next({
      folderId,
      id: ++this.nextRequestId,
      openAfterRenameSubmit: false,
      rename: false,
      renameDraft: {},
      source: options.source ?? 'unknown',
      workspaceId: options.workspaceId,
    });
  }

  clear(requestId: number) {
    if (this.request$.value?.id === requestId) {
      this.request$.next(null);
    }
  }
}
